// Tin thị trường (Mint-MarketNews): lọc tin, thông báo hãng sắp hiệu lực, thống kê chỉ số cước, bộ lọc nguồn feed.
import { daysBetween } from '@hana/domain';
import type { CarrierNotice, FeedSource, MarketIndex, NewsItem } from './types';

/** Điểm liên quan tối thiểu của bộ lọc "≥ 3". */
export const RELEVANT_MIN = 3;

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();

export interface NewsFilter {
  q: string;
  source: string | null;
  relevantOnly: boolean;
  hidden: ReadonlySet<string>;
}

/** Tin mới nhất trước; tìm trên cả tiêu đề gốc, tiêu đề Việt, tóm tắt và hãng. */
export function filterNews(items: readonly NewsItem[], f: NewsFilter): NewsItem[] {
  const q = fold(f.q.trim());
  return items
    .filter((n) => !f.hidden.has(n.id) && (!f.source || n.source === f.source) && (!f.relevantOnly || n.relevance >= RELEVANT_MIN))
    .filter((n) => !q || fold(`${n.title} ${n.titleVi} ${n.summary} ${n.carriers.join(' ')}`).includes(q))
    .sort((a, b) => b.at.localeCompare(a.at));
}

/** Thông báo hiệu lực trong `days` ngày tới (kể cả hôm nay), sớm nhất trước. */
export function noticesSoon(items: readonly CarrierNotice[], today: string, days = 7): CarrierNotice[] {
  return items
    .filter((n) => {
      const d = daysBetween(today, n.effective);
      return d >= 0 && d <= days;
    })
    .sort((a, b) => a.effective.localeCompare(b.effective));
}

/** `3` → "còn 3 ngày"; hôm nay → "hôm nay"; đã qua → "đã hiệu lực". */
export function effectiveLabel(effective: string, today: string): string {
  const d = daysBetween(today, effective);
  return d < 0 ? 'đã hiệu lực' : d === 0 ? 'hôm nay' : `còn ${d} ngày`;
}

export interface IndexStat {
  last: number;
  prev: number;
  delta: number;
  /** % đổi so kỳ trước, 1 chữ số */
  pct: number;
}

export function indexStat(i: Pick<MarketIndex, 'values'>): IndexStat {
  const v = i.values;
  const last = v[v.length - 1] ?? 0;
  const prev = v[v.length - 2] ?? last;
  return { last, prev, delta: last - prev, pct: prev ? Math.round(((last - prev) / prev) * 1000) / 10 : 0 };
}

/** Bộ lọc regex của nguồn (`splash247`: `container|port|freight`); trống = nhận hết, regex hỏng = nhận hết. */
export function passesFeedFilter(pattern: string, text: string): boolean {
  if (!pattern.trim()) return true;
  try {
    return new RegExp(pattern, 'i').test(text);
  } catch {
    return true;
  }
}

export const feedErrors = (sources: readonly FeedSource[]): number => sources.filter((s) => s.enabled && s.ok === false).length;

export const SOURCE_NAMES: Record<string, string> = {
  splash247: 'Splash247',
  loadstar: 'The Loadstar',
  gcaptain: 'gCaptain',
  'container-news': 'Container News',
  'vnexpress-kd': 'VnExpress',
  'mail-notice': 'Email NOTICE (info@)',
};

export const FEED_KIND: Record<FeedSource['kind'], string> = { rss: 'RSS tin', index_page: 'Chỉ số · trang', index_news: 'Chỉ số · báo chí', carrier: 'Hãng tàu' };

export type FeedFormKey = 'id' | 'name' | 'url' | 'match' | 'tag';

/** Trường của ngăn Thêm / Sửa nguồn theo loại (board `dw.fields`); `req` = bắt buộc. */
export function feedFields(kind: FeedSource['kind'], isNew: boolean): { key: FeedFormKey; label: string; req: boolean; wide: boolean; ph?: string; help?: string }[] {
  const out: { key: FeedFormKey; label: string; req: boolean; wide: boolean; ph?: string; help?: string }[] = [];
  if (isNew) out.push({ key: 'id', label: 'Mã nguồn', req: true, wide: false, ph: 'vd: vneconomy', help: 'chữ thường, không dấu · không đổi được sau khi tạo' });
  out.push({ key: 'name', label: 'Tên hiển thị', req: true, wide: !isNew });
  if (kind !== 'index_news') out.push({ key: 'url', label: kind === 'carrier' ? 'Trang tin / thông báo khách hàng' : 'URL', req: kind !== 'carrier', wide: true, ph: 'https://' });
  if (kind === 'rss' || kind === 'index_news') {
    out.push({ key: 'match', label: 'Lọc tiêu đề (regex)', req: false, wide: true, help: kind === 'rss' ? 'chỉ lấy bài có tiêu đề / mô tả khớp, vd container|cảng|cước · trống = lấy hết' : 'nhận bài báo nhắc chỉ số, vd \\bSCFI\\b|Shanghai Containerized' });
  }
  if (kind === 'index_page' || kind === 'index_news') out.push({ key: 'tag', label: 'Mã chỉ số', req: true, wide: false, help: 'WCI · FBX · SCFI · VCFI' });
  if (kind === 'carrier') out.push({ key: 'tag', label: 'Mã hãng (CarrierCode)', req: true, wide: false });
  return out;
}

/** Các trường bắt buộc còn trống. */
export function feedMissing(kind: FeedSource['kind'], isNew: boolean, vals: Partial<Record<FeedFormKey, string>>): FeedFormKey[] {
  return feedFields(kind, isNew).filter((f) => f.req && !(vals[f.key] ?? '').trim()).map((f) => f.key);
}

/** Tuyến mẫu của "Chỉ số theo tuyến" (board Mint-MarketNews: RT). Giá kỳ gần nhất, USD/FEU. */
export const INDEX_ROUTES: readonly (readonly [string, number])[] = [
  ['Thượng Hải → Rotterdam', 3150],
  ['Thượng Hải → Los Angeles', 2420],
  ['Thượng Hải → New York', 3380],
  ['Thượng Hải → Genoa', 3010],
  ['Thượng Hải → Busan', 310],
];

export interface RouteIndexRow { route: string; values: number[]; last: number; prev: number; pct: number }

/** Chuỗi 8 kỳ của từng tuyến = chuỗi chỉ số chọn, co giãn về giá tuyến, lệch nhẹ theo thứ tự (như board). */
export function routeIndexRows(base: readonly number[], routes: readonly (readonly [string, number])[] = INDEX_ROUTES): RouteIndexRow[] {
  const end = base[base.length - 1] || 1;
  return routes.map(([route, v0], i) => {
    const values = base.map((b) => Math.round(((v0 * b) / end) * (1 + (((i * 7) % 5) - 2) / 100)));
    const last = values[values.length - 1] ?? 0;
    const prev = values[values.length - 2] ?? last;
    return { route, values, last, prev, pct: prev ? Math.round(((last - prev) / prev) * 1000) / 10 : 0 };
  });
}
